/*
 *
 * ManageUserModal
 *
 * @version 1.0.0
 * @author Christopher Monti
 * @since 15-Jan-2025
 */

import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import MerchantModal from '@nexidigital/merchantmodal'
import PortalSelect from '@nexidigital/portalselect'
import PortalSwitch from '@nexidigital/portalswitch'
import PortalTextInput from '@nexidigital/portaltextinput'
import { userMainRoles } from 'Utility/globalMethods'
import { validators } from 'Utility/validatorForm' 
import { ELIGIBLE_ROLES_USERS, USER_STATUSES } from 'Schemas/constants'
import formattedMessage, { captions, tooltips, messages } from './languageModule'
import './ManageUserModal.scss'

const ManageUserModal = ({ userId, data, isLoading, actions }) => {
  const intl = useIntl()
  const isEditingItself = useMemo(() => userId == data?.id, [userId, data?.id])

  const formik = useFormik({
    initialValues: {
      id: data?.id,
      userName: data?.userName,
      email: data?.email || '',
      name: data?.name || '',
      role: userMainRoles(data?.roles) || '',
      status: data?.status || 'ACTIVATED'
    },
    validationSchema: Yup.object({
      email: Yup.string().email(intl.formatMessage(captions.validEmail)).required().test(
        'custom-validation',
        intl.formatMessage(captions.validEmail),
        function (value) {
          return validators.isEmail(value)
        }
      ),
      name: Yup.string().required(),
      role: Yup.string().oneOf(ELIGIBLE_ROLES_USERS).required(intl.formatMessage(captions.validRole)),
      status: Yup.string().oneOf(USER_STATUSES).required()
    })
  })
  const onSubmit = (values) => {
    actions.submitForm({ name: 'ManageUserForm', data: values })
  }

  const roleOptions = useMemo(
    () => ELIGIBLE_ROLES_USERS.map((role) => ({ value: role, label: intl.formatMessage(captions[role]) })),
    [ELIGIBLE_ROLES_USERS]
  )

  const submitDisabled = useMemo(
    () => !formik.dirty || !formik.isValid || !formik.values.email || !formik.values.name || !formik.values.role,
    [formik.dirty, formik.isValid, formik.values.email, formik.values.name, formik.values.role]
  )

  return (
    <MerchantModal
      closeLabel={intl.formatMessage(captions.close)}
      bigPopup
      topBarTitle={formik.values.id ? formattedMessage.titleManage : formattedMessage.titleCreate}
      onClose={() => actions.closePopup()}
      primaryButtonLabel={formik.values.id ? formattedMessage.confirm : formattedMessage.create}
      primaryAction={() => onSubmit(formik.values)}
      primaryButtonDisabled={submitDisabled}
      primaryButtonLoading={isLoading}>
      <MerchantGenericCard>
        <div className='user-modal-form'>
          <div className='form-section'>
            <div className='form-row switch'>
              <h5 className='subtitle'>{formattedMessage.generalInfo}</h5>
              <PortalSwitch
                id='switchUserStatus'
                ariaLabel={intl.formatMessage(captions.status)}
                checked={formik.values.status === 'ACTIVATED'}
                activeLabel={formattedMessage.activated}
                disabled={isEditingItself || !data?.status}
                deactiveLabel={formattedMessage.deactivated}
                onChange={(selectedOption) =>
                  formik.setFieldValue('status', selectedOption ? 'ACTIVATED' : 'DEACTIVATED')
                }
                error={formik.touched.status && formik.errors.status}
                errorText={formik.errors.status}
              />
            </div>
            <div className='form-row'>
              <PortalTextInput
                ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)}
                label={intl.formatMessage(captions.email)}
                id='textInputEmail'
                type='email'
                disabled={!!formik.values.id}
                onChange={(e) => formik.setFieldValue('email', e.target.value)}
                onFocus={() => formik.setFieldTouched('email', false)}
                onBlur={(e) => formik.setFieldTouched('email', e.target.value ? true : false)}
                error={!!(formik.touched.email && formik.errors.email)}
                errorText={formik.errors.email}
                value={formik.values.email}
                placeholderBlank
                autoComplete='email'
              />
              <PortalTextInput
                ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)}
                label={intl.formatMessage(captions.name)}
                id='textInputCustomField'
                type='text'
                onChange={(e) => formik.setFieldValue('name', e.target.value)}
                onFocus={() => formik.setFieldTouched('name', false)}
                onBlur={(e) => formik.setFieldTouched('name', e.target.value ? true : false)}
                error={!!(formik.touched.name && formik.errors.name)}
                errorText={formik.errors.name}
                value={formik.values.name}
                placeholderBlank
                autoComplete='name'
              />
              <PortalSelect
                id='selectUserRole'
                disabled={isEditingItself}
                label={formattedMessage.role}
                ariaLabelIconButton={intl.formatMessage(captions.role)}
                value={formik.values.role}
                options={roleOptions}
                onChange={(selectedOption) => formik.setFieldValue('role', selectedOption?.value || '')}
                onFocus={() => formik.setFieldTouched('role', false)}
                onBlur={(e) => formik.setFieldTouched('role', e.target.value ? true : false)}
                error={formik.touched.role && formik.errors.role}
                errorText={formik.errors.role}
                noSearch
                placeholderBlank
                labelIcon='info'
                infoToShow={[
                  {
                    title: intl.formatMessage(tooltips.roleTooltipMainTitle)
                  },
                  {
                    title: intl.formatMessage(tooltips.roleTooltipAdminTitle),
                    value: intl.formatMessage(tooltips.roleTooltipAdminDescription)
                  },
                  {
                    title: intl.formatMessage(tooltips.roleTooltipOperatorTitle),
                    value: intl.formatMessage(tooltips.roleTooltipOperatorDescription)
                  },
                  {
                    title: intl.formatMessage(tooltips.roleTooltipViewerTitle),
                    value: intl.formatMessage(tooltips.roleTooltipViewerDescription)
                  }
                ]}
                textTooltip={`${intl.formatMessage(tooltips.roleTooltipMainTitle)}
                  ${intl.formatMessage(tooltips.roleTooltipAdminTitle)}${intl.formatMessage(
      tooltips.roleTooltipAdminDescription
    )} ${intl.formatMessage(tooltips.roleTooltipOperatorTitle)}${intl.formatMessage(
      tooltips.roleTooltipOperatorDescription
    )} ${intl.formatMessage(tooltips.roleTooltipViewerTitle)}${intl.formatMessage(
      tooltips.roleTooltipViewerDescription
    )}`}
              />
            </div>
          </div>
        </div>
      </MerchantGenericCard>
    </MerchantModal>
  )
}

ManageUserModal.propTypes = {
  userId: PropTypes.string,
  actions: PropTypes.object,
  data: PropTypes.object,
  isLoading: PropTypes.bool
}

ManageUserModal.defaultProps = {
  userId: '',
  actions: {},
  data: {},
  isLoading: false
}

export default ManageUserModal
